import { COLISSIMO_SERVICES, shipmentSchema, type ShipmentInput } from "@erp/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle2, ChevronLeft, Truck } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "~/components/ui/button";
import { Field } from "~/components/ui/field";
import { Input } from "~/components/ui/input";
import { Page, PageHeader, Panel, Segmented } from "~/components/ui/page";
import { Select } from "~/components/ui/select";
import { EmptyState, ErrorState, Skeleton } from "~/components/ui/states";
import type { Carrier } from "~/features/shipments/parcel-fields";
import { newParcelDraft, ParcelsEditor, parcelsPayload, type ParcelDraft } from "~/features/shipments/parcels-editor";
import { usePrint } from "~/lib/auto-print";
import { api, unwrap } from "~/lib/api";
import { cn } from "~/lib/cn";
import { ApiError } from "~/lib/api";
import { errorDetails, errorMessageKey, fieldErrors } from "~/lib/errors";
import { Confirm } from "~/components/ui/confirm";
import { errorAt, schemaErrors, type ErrorMap } from "~/lib/forms";
import { useFormat, useT } from "~/lib/i18n";
import { SerialPicker, useSerialTracking } from "~/features/stock/serial-input";
import { HelpTip } from "~/components/ui/help-tip";

const search = z.object({
  orderId: z.uuid(),
  /** « Expédier la sélection » from the order: the quantities picked there, as "lineId:quantity,…". */
  pick: z.string().max(4000).optional(),
});

export const Route = createFileRoute("/_app/shipments/new")({ validateSearch: search, component: NewShipment });

const fetchDraft = (orderId: string) => unwrap(api.shipments.draft.get({ query: { orderId } }));
type Draft = Awaited<ReturnType<typeof fetchDraft>>;

function BackLink({ orderId }: { orderId: string }) {
  const t = useT();
  return (
    <Link
      to="/orders/$id"
      params={{ id: orderId }}
      className="mb-2 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg"
    >
      <ChevronLeft className="size-4" aria-hidden />
      {t("shipments.actions.openOrder")}
    </Link>
  );
}

function NewShipment() {
  const { orderId, pick } = Route.useSearch();
  const query = useQuery({ queryKey: ["shipments", "draft", orderId], queryFn: () => fetchDraft(orderId) });
  if (query.isPending)
    return (
      <Page>
        <BackLink orderId={orderId} />
        <Skeleton className="h-8 w-60" />
        <Skeleton className="mt-6 h-96 w-full" />
      </Page>
    );
  if (query.isError)
    return (
      <Page>
        <BackLink orderId={orderId} />
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </Page>
    );
  return <ShipmentForm draft={query.data} pick={pick} />;
}

type Service = (typeof COLISSIMO_SERVICES)[number];

function ShipmentForm({ draft, pick }: { draft: Draft; pick?: string }) {
  const t = useT();
  const format = useFormat();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const open = draft.lines.filter((l) => l.remaining > 0);
  const picked = new Map((pick ?? "").split(",").flatMap((p) => (p.includes(":") ? [p.split(":") as [string, string]] : [])));
  const [quantities, setQuantities] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      open.map((l) => [
        l.id,
        pick ? String(Math.min(l.remaining, Number.parseInt(picked.get(l.id) ?? "0", 10) || 0)) : String(l.remaining),
      ]),
    ),
  );
  // Tracked articles leave unit by unit: the parcel names the ones picked.
  const serialTracking = useSerialTracking();
  const [serials, setSerials] = useState<Record<string, string[]>>({});
  const tracked = (l: Draft["lines"][number]) => serialTracking && l.serialTracked && l.variantId !== null;
  const labelCarriers = { colissimo: draft.colissimo, chronopost: draft.chronopost };
  const available = (["colissimo", "chronopost", "other"] as const).filter((c) => c === "other" || labelCarriers[c]);
  const [carrier, setCarrier] = useState<Carrier>(available[0] ?? "other");
  const [service, setService] = useState<Service>("DOM");
  const [generateLabel, setGenerateLabel] = useState(available[0] !== "other");
  const [tracking, setTracking] = useState("");
  const [parcels, setParcels] = useState<ParcelDraft[]>(() => [newParcelDraft(draft.boxes)]);
  const [submitted, setSubmitted] = useState(false);
  const [serverErrors, setServerErrors] = useState<ErrorMap>({});

  const qty = (id: string) => {
    const n = Number.parseInt(quantities[id] ?? "0", 10);
    return Number.isFinite(n) ? n : Number.NaN;
  };
  // Articles' weight as La Poste counts it (own weight, family average, kits added up).
  const articlesGrams = open.reduce((sum, l) => sum + (l.weightGrams ?? 0) * Math.max(0, qty(l.id) || 0), 0);
  const parcelValues = parcelsPayload(parcels, articlesGrams, draft.boxes);
  const totalGrams = parcelValues.reduce((sum, p) => sum + p.weightGrams, 0);
  const short = open.filter((l) => l.variantId && qty(l.id) > (l.onHand ?? 0)).length;

  const payload: ShipmentInput = {
    orderId: draft.order.id,
    lines: open
      .map((l) => ({ orderLineId: l.id, quantity: qty(l.id), ...(tracked(l) ? { serials: serials[l.id] ?? [] } : {}) }))
      .filter((l) => l.quantity !== 0),
    carrier,
    service,
    weightGrams: totalGrams,
    parcels: parcelValues,
    trackingNumber: tracking,
    generateLabel: carrier !== "other" && generateLabel,
  };
  const errors = { ...serverErrors, ...(submitted ? schemaErrors(shipmentSchema, payload) : {}) };
  const overLine = open.find((l) => qty(l.id) > l.remaining || qty(l.id) < 0 || Number.isNaN(qty(l.id)));

  // Units set aside for other orders are only taken once the user confirms it (they are released from them).
  const [reserved, setReserved] = useState<Record<string, string> | null>(null);
  const printer = usePrint();
  const ship = useMutation({
    mutationFn: (takeReserved: boolean = false) => unwrap(api.shipments.post(shipmentSchema.parse({ ...payload, takeReserved }))),
    onSuccess: (shipment) => {
      for (const key of ["orders", "stock", "shipments"]) void queryClient.invalidateQueries({ queryKey: [key] });
      toast.success(
        shipment.labelKey && printer.labelsAuto
          ? `${t("shipments.toasts.shipped", { number: shipment.number })} ${t("printing.labelAuto")}`
          : t("shipments.toasts.shipped", { number: shipment.number }),
      );
      // Back to the order, not the shipment: the user keeps preparing the rest of it.
      void navigate({ to: "/orders/$id", params: { id: draft.order.id } });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.body?.code === "stock.reserved_for_other_orders") {
        setReserved(errorDetails(error));
        return;
      }
      setServerErrors(fieldErrors(error));
      toast.error(t(errorMessageKey(error)));
    },
  });

  const submit = () => {
    setSubmitted(true);
    setServerErrors({});
    if (overLine || Object.keys(schemaErrors(shipmentSchema, payload)).length) return;
    ship.mutate(false);
  };

  const address = draft.order.party.shippingAddress ?? draft.order.party.billingAddress;
  // The label goes to the person receiving the goods when the order names one.
  const recipient = draft.order.party.recipient;

  return (
    <Page>
      <PageHeader
        back={<BackLink orderId={draft.order.id} />}
        title={t("shipments.newTitle")}
        meta={
          <span className="tabular">
            {t("shipments.newSubtitle", { number: draft.order.number })}
            {draft.order.externalReference ? ` — ${draft.order.externalReference}` : ""}
          </span>
        }
      />

      {open.length === 0 ? (
        <Panel>
          <EmptyState icon={<CheckCircle2 />} title={t("shipments.hints.nothingToShip")} />
        </Panel>
      ) : (
        <form
          className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_340px]"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <Panel>
            <div className="flex items-center justify-between gap-2 px-5 pt-5 md:px-6">
              <h2 className="text-[15px] font-medium">{t("shipments.sections.lines")}</h2>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setQuantities(Object.fromEntries(open.map((l) => [l.id, String(l.remaining)])))}
                >
                  {t("shipments.actions.shipAll")}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setQuantities(Object.fromEntries(open.map((l) => [l.id, "0"])))}>
                  {t("shipments.actions.shipNone")}
                </Button>
              </div>
            </div>
            {short > 0 ? (
              <p className="mx-5 mt-4 flex items-start gap-2 rounded-panel bg-warning-soft px-4 py-3 text-sm text-warning md:mx-6">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
                {t("shipments.hints.stockShort", { count: short })}
              </p>
            ) : null}
            <ul className="mt-3 flex flex-col">
              {open.map((l) => {
                const value = qty(l.id);
                const invalid = Number.isNaN(value) || value < 0 || value > l.remaining;
                const lacking = l.variantId !== null && value > (l.onHand ?? 0);
                return (
                  <li key={l.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line px-5 py-3 md:px-6">
                    <div className="min-w-0 flex-1 basis-60">
                      <p className="text-sm">{l.label}</p>
                      {l.sku ? <p className="text-xs text-fg-3 tabular">{l.sku}</p> : null}
                    </div>
                    <dl className="flex gap-4 text-sm">
                      <div className="text-right">
                        <dt className="text-xs text-fg-3">{t("shipments.fields.remaining")}</dt>
                        <dd className="tabular">{format.number(l.remaining)}</dd>
                      </div>
                      <div className="text-right">
                        <dt className="text-xs text-fg-3">{t("shipments.fields.onHand")}</dt>
                        <dd className={cn("tabular", lacking ? "text-warning" : "text-fg-2")}>
                          {l.onHand === null ? "" : format.number(l.onHand)}
                        </dd>
                      </div>
                    </dl>
                    <label className="flex items-center gap-2">
                      <span className="text-xs text-fg-3">{t("shipments.fields.toShip")}</span>
                      <Input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={l.remaining}
                        value={quantities[l.id] ?? ""}
                        aria-invalid={submitted && invalid ? true : undefined}
                        onChange={(e) => setQuantities((q) => ({ ...q, [l.id]: e.target.value }))}
                        className="w-20 text-right tabular"
                      />
                    </label>
                    {tracked(l) && value > 0 ? (
                      <div className="basis-full">
                        <SerialPicker
                          variantId={l.variantId!}
                          value={serials[l.id] ?? []}
                          onChange={(next) => setSerials((s) => ({ ...s, [l.id]: next }))}
                          expected={value}
                        />
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
            {submitted && (overLine || errors.lines) ? (
              <p role="alert" className="border-t border-line px-5 py-3 text-xs font-medium text-danger md:px-6">
                {t(overLine ? "errors.codes.shipment.exceeds_remaining" : "errors.codes.shipment.empty")}
              </p>
            ) : null}
          </Panel>

          <aside className="flex flex-col gap-5">
            <Panel className="flex flex-col gap-4 p-5 md:p-6">
              <h2 className="text-[15px] font-medium">{t("shipments.sections.parcel")}</h2>
              {available.length > 1 ? (
                <Segmented
                  label={t("shipments.fields.carrier")}
                  value={carrier}
                  onChange={setCarrier}
                  options={available.map((c) => ({ value: c, label: t(`shipments.carriers.${c}`) }))}
                />
              ) : (
                <p className="rounded-panel bg-surface-2 px-3 py-2.5 text-sm text-fg-2">{t("shipments.hints.noColissimo")}</p>
              )}
              {carrier !== "other" ? (
                <>
                  {carrier === "colissimo" ? (
                    <Field label={t("shipments.fields.service")}>
                      <Select
                        value={service}
                        onValueChange={(v) => setService(v as Service)}
                        options={COLISSIMO_SERVICES.map((s) => ({ value: s, label: t(`shipments.services.${s}`) }))}
                      />
                    </Field>
                  ) : null}
                  <label className="flex min-h-11 cursor-pointer items-start gap-2.5 text-sm md:min-h-0">
                    <input
                      type="checkbox"
                      checked={generateLabel}
                      onChange={(e) => setGenerateLabel(e.target.checked)}
                      className="mt-0.5 size-4 accent-[var(--brand-gold)]"
                    />
                    <span>
                      {t(carrier === "chronopost" ? "shipments.hints.generateLabelChronopost" : "shipments.hints.generateLabel")}
                      <span className="block text-xs text-fg-3">{t("shipments.hints.generateLabelHint")}</span>
                    </span>
                  </label>
                </>
              ) : null}
              <ParcelsEditor
                value={parcels}
                onChange={setParcels}
                boxes={draft.boxes}
                articlesGrams={articlesGrams}
                errors={(index, field) => errorAt(t, errors, `parcels.${index}.${field}`)}
              />
              {!(carrier !== "other" && generateLabel) ? (
                <Field
                  label={t("shipments.fields.tracking")}
                  hint={t("shipments.hints.trackingOptional")}
                  error={errorAt(t, errors, "trackingNumber")}
                >
                  <Input value={tracking} onChange={(e) => setTracking(e.target.value)} autoComplete="off" className="tabular" />
                </Field>
              ) : null}
            </Panel>

            <Panel className="p-5 md:p-6">
              <h2 className="mb-2 text-[15px] font-medium">{t("shipments.sections.recipient")}</h2>
              <p className="text-sm font-medium">{recipient?.name ?? draft.order.party.name}</p>
              {recipient?.company ? <p className="text-sm text-fg-2">{recipient.company}</p> : null}
              <address className="text-sm not-italic leading-relaxed text-fg-2">
                {address
                  ? format.address(address).map((line) => (
                      <span key={line} className="block">
                        {line}
                      </span>
                    ))
                  : t("common.states.none")}
              </address>
              {(recipient?.phone ?? draft.order.party.phone) ? (
                <p className="mt-1 text-sm text-fg-2 tabular">{recipient?.phone ?? draft.order.party.phone}</p>
              ) : null}
            </Panel>

            <div className="flex flex-col gap-2">
              <Button type="submit" variant="primary" size="lg" loading={ship.isPending}>
                <Truck />
                {t("shipments.actions.ship")}
              </Button>
              <div className="flex items-center justify-center gap-0.5">
                <p className="text-center text-xs text-fg-3">{t("shipments.hints.stockLeaves")}</p>
                <HelpTip id="shipment.ship" />
              </div>
            </div>
          </aside>
        </form>
      )}
      <Confirm
        open={reserved !== null}
        onOpenChange={(open) => !open && setReserved(null)}
        title={t("stock.reserved.title")}
        body={t("errors.codes.stock.reserved_for_other_orders", reserved ?? {})}
        confirmLabel={t("stock.reserved.confirm")}
        loading={ship.isPending}
        onConfirm={() => {
          setReserved(null);
          ship.mutate(true);
        }}
      />
    </Page>
  );
}
